import { ChapterRecords } from "./ChapterRecords.js";
import { trackerEvents } from "../constants.js";
import { device } from "./Store.js";

class ReadStream {
	constructor(epub_id, v, chapterid, dT, heartbeat, stream, rT, app, appVersion) {
		this.epub_id = epub_id;
		this.v = v;
		this.chapterid = chapterid;
		this.dT = dT;
		this.heartbeat = heartbeat;
		this.stream = stream;
		this.app = app;
		this.appVersion = appVersion;
		this.screen = {
			width: window.screen.width,
			height: window.screen.height,
		};
		this.pixelRatio = window.devicePixelRatio;
		this.deviceId = device.id;
		this.rT = rT;
	}
}

export class Report {
	constructor(
		tracks,
		epub_id,
		v,
		chapterid,
		dT,
		heartbeat,
		app,
		appVersion,
		deviceId,
		rT,
	) {
		this.tracks = tracks;
		this.epub_id = epub_id;
		this.v = v;
		this.chapterid = chapterid;
		this.dT = dT;
		this.heartbeat = heartbeat;
		this.app = app;
		this.appVersion = appVersion;
		this.deviceId = deviceId;
		this.rT = rT;
	}

	/** Filters records to send */
	streamFilter = (value) => {
		const heartbeatRate = this.heartbeat;
		let elapsed;
		if (value.eT) {
			elapsed = new Date(value.eT).getTime() - new Date(value.sT).getTime();
		} else {
			elapsed = -1;
		}
		value.eT = undefined;

		const isUnloadedOrEarly =
			value.tE === trackerEvents.Completed ||
			value.tE === trackerEvents.Terminated ||
			value.tE === trackerEvents.ModalPaused ||
			value.tE === trackerEvents.TocNav ||
			value.tE === trackerEvents.BackNav ||
			value.I !== undefined ||
			value.rE !== undefined ||
			value.sE !== undefined ||
			value.pE !== undefined ||
			value.aE !== undefined;

		if (isUnloadedOrEarly) {
			if (value.h === undefined) {
				value.h = 1;
			}
			return true;
		} else if (elapsed <= 1000) {
			return false;
		} else if (value.h === undefined || !elapsed) {
			return false;
		} else if (Number.isNaN(value.h)) {
			value.h = elapsed / heartbeatRate;
			return true;
		} else {
			return true;
		}
	};

	/**
	 * Returns a filtered stream of sessions for the payload. It also notes,
	 * per chapter, the ended records it looked at, so that removeEnded()
	 * can drop them once the payload has been delivered: the stream would
	 * otherwise re-send every session ever recorded, and outgrow the 64 KiB
	 * keepalive limit. Ended records the filter rejects are dropped too;
	 * the filter would reject them on every later post as well.
	 */
	generateStream(chapters, epub_id) {
		this.ended = new Map();
		return chapters
			.flatMap((chapter) => {
				const record = new ChapterRecords(chapter, epub_id);
				const current = record.getIncrement();
				const entries = record.getSessionEntries();
				this.ended.set(
					chapter,
					entries
						.filter(({ index }) => index < current)
						.map(({ index }) => index),
				);
				return entries.map(({ session }) => session);
			})
			.filter(this.streamFilter);
	}

	/** Removes the ended records the last generated stream covered. */
	removeEnded() {
		for (const [chapter, indices] of this.ended || []) {
			new ChapterRecords(chapter, this.epub_id).removeSessions(indices);
		}
	}

	/** Wraps the reading stream and returns a payload */
	wrapStream() {
		const stream = this.generateStream(this.tracks, this.epub_id);
		if (stream.length > 0) {
			const payload = new ReadStream(
				this.epub_id,
				this.v,
				this.chapterid,
				this.dT,
				this.heartbeat,
				stream,
				this.rT,
				this.app,
				this.appVersion,
			);
			return payload;
		} else {
			return null;
		}
	}
}
